import { Component, Inject } from "@angular/core";
import {
    NmsWizardStepModel,
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import { ANGULARJS_TRANSLATE,
    MONITORING_SERVICE,
    NG_DIALOG
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Device } from "@nms-ng2/app/modules/device/device-backup/device-backup.models";
import { DwdmDevice, MuxDemux, MuxDemuxDevicesModel, MuxDemuxInfoModel } from "../../../mux-demux.model";
import { MuxDemuxService } from "../../../services/mux-demux.service";
import { DialogService } from "@nms-ng2/app/shared/services/dialog/dialog.service";
import { MuxDemuxDevicesTabConverter } from "./services/mux-demux-devices-tab-converter";
import { NmsCustomTextConfig } from "@nms-angular-toolkit/nms-custom-text";

/*
* Componente responsável por renderizar a aba de equipamentos do Mux/Demux.
*/
@Component({
    selector: "mux-demux-devices-tab",
    templateUrl: "./mux-demux-devices-tab.component.html",
    styleUrls: ["./mux-demux-devices-tab.component.scss"]
})
export class MuxDemuxDevicesTabComponent extends AbstractNmsWizardStep {
    model: MuxDemuxDevicesModel = new MuxDemuxDevicesModel();
    deviceName: string = "";
    activeDevices: Device[] = [];
    descriptionTextTipicon: NmsCustomTextConfig;

    constructor(@Inject(NG_DIALOG) private readonly ngDialog: any,
                @Inject(MONITORING_SERVICE) private readonly monitoringService: any,
                @Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
                private readonly dialogService: DialogService,
                private readonly muxDemuxService: MuxDemuxService,
                private readonly muxDemuxDevicesTabConverter: MuxDemuxDevicesTabConverter) {
        super();
        this.descriptionTextTipicon = {
            id: "mux-text-description-tipicon",
            text: this.translate.instant("mux.demux.modal.description"),
            hasTooltip: true,
            tooltipContent: this.translate.instant("mux.demux.modal.description.tooltip"),
        };
    }

    addDevice(): void {
        if (this.model.devices && this.model.devices.length >= 2) {
            return;
        }

        const applyDevice = (selectedDevices) => {
            if (!this.validateMaxSelectedDevices(selectedDevices)) {
                const dialogMessage = { translateKey: "add.mux.demux.modal.error.too.many.devices" };
                this.dialogService.openDialog(dialogMessage)

                return;
            }

            selectedDevices.forEach((device: Device) => {
                if (device && device.id) {
                    const dwdmDevice: DwdmDevice = {
                        resourceId: device.id,
                        name: device.name
                    };

                    this.model.devices.push(dwdmDevice);
                    this.activeDevices.push(device);
                }
            });
        };

        this.openDevicesModal(applyDevice);
    }

    private validateMaxSelectedDevices(selectedDevice): boolean {
        let totalDevices = 0;
        if (this.model.devices && this.model.devices.length > 0) {
            totalDevices = this.model.devices.length;
        }
        if (selectedDevice && selectedDevice.length > 0) {
            totalDevices += selectedDevice.length;
        }

        return totalDevices <= 2;
    }

    removeDevice(dwdmDevice: DwdmDevice): void {
        this.model.devices = this.model.devices.filter(device => device !== dwdmDevice);
        this.activeDevices = this.activeDevices.filter(device => device.id !== dwdmDevice.resourceId);
    }

    confirmRemoveAllDevices(): void {
        if (!this.hasDevices()) {
            return;
        }

        const confirmMessage = "mux.demux.modal.confirm.remove.all.devices";
        const onConfirmCallback = () => {
            this.model.devices = [];
            this.activeDevices = [];
        };
        this.showConfirmationDialog(confirmMessage, onConfirmCallback);
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        return Promise.resolve(this.model);
    }

    goingToPreviousStep(): Promise<NmsWizardStepModel> {
        return Promise.resolve(this.model);
    }

    async comingFromPreviousStep(muxinfoModel: MuxDemuxInfoModel,
                                     wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        this.model = this.muxDemuxDevicesTabConverter.convert(muxinfoModel, wizardStepsCacheMap)
        return Promise.resolve(this.model);
    }

    async comingFromNextStep(muxinfoModel: MuxDemuxInfoModel,
                             wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        wizardStepsCacheMap["MuxDemuxInfoModel"] = muxinfoModel;
        return Promise.resolve(this.model);
    }

    async allowPrevious(): Promise<NmsWizardStepValidationResponse> {
        return Promise.resolve(NmsWizardStepValidationResponse.getDefaultValidationResponse());
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        return Promise.resolve(NmsWizardStepValidationResponse.getDefaultValidationResponse());
    }

    async allowFinish(): Promise<NmsWizardStepValidationResponse> {
        if (this.hasDevices()) {
            return new NmsWizardStepValidationResponse(true);
        }

        const confirmationMessage = "mux.demux.modal.confirm.finish.empty.devices";
        return new Promise((resolve) => {
            this.showConfirmationDialog(confirmationMessage, () => {
                resolve(new NmsWizardStepValidationResponse(true));
            });
        });
    }

    async finishAction(wizardStepsCacheMap: NmsWizardStepMapData): Promise<void> {
        wizardStepsCacheMap["MuxDemuxDevicesModel"] = this.model;
        const muxDemux: MuxDemux = this.muxDemuxService.buildMuxDemux(wizardStepsCacheMap);

        try {
            if (muxDemux.resourceId) {
                this.muxDemuxService.updateMuxDemux(muxDemux);
            } else {
                this.muxDemuxService.saveMuxDemux(muxDemux);
            }
        } finally {
            this.loadingConfig.emit({ showLoading: false });
        }
    }

    openDevicesModal(callback) {
        this.ngDialog
            .openConfirm({
                template: "templates/features/template/template-instance/modals/add-device-modal.html",
                controller: "AddDeviceModalGenericCtrl",
                className: "extra-large-modal",
                closeByNavigation: true,
                resolve: {
                    devicesAlreadyAdded: () => (this.activeDevices ? this.activeDevices : []),
                    deviceModelRestrictionTypes: () => {},
                    monitoringSettings: () => this.monitoringService.getMonitoringSettings(),
                    allowOnlyOneEquipment: () => false,
                    allowOnlyPolledEquipment: () => false
                }
            })
            .then((selectedDevices: Device[]) => {
                return callback(selectedDevices);
            });
    }

    hasDevices(): boolean {
        return this.model.devices && this.model.devices.length > 0;
    }

    private showConfirmationDialog(confirmMessage: string, onConfirm: () => void, onCancel?: () => void): Promise<boolean> {
        const confirmationModalFn = () => true;

        return new Promise<boolean>((resolve) => {
            const confirmationCallbackFn = () => {
                onConfirm();
                resolve(true);
            };
            const cancelCallbackFn = () => {
                if (onCancel) {
                    onCancel();
                }
                resolve(false);
            };

            this.dialogService.confirm(
                confirmationModalFn,
                confirmMessage,
                confirmationCallbackFn,
                cancelCallbackFn
            );
        });
    }
}
